import React, { useEffect, useState } from 'react'
import CommonLayout from '../../../layouts/CommonLayout'
import { Modal, Spinner, Button } from "react-bootstrap";
import { allAPiServicesCall } from '../../../services/apiServices';
import { getAuthConfig } from '../../../services/apiUtils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { artistImageUrl } from '../../../utils/utils';
import {
  FaUser,
  FaSearch,
  FaUsers,
  FaEye,
  FaEdit,
  FaChevronLeft,
  FaChevronRight,
  FaMicrophone,
} from "react-icons/fa";
import './ArtistBio.css';

// Helper to decode URL-encoded artist names
const decodeArtistName = (name) => {
  if (!name) return 'Unknown Artist';
  try {
    return decodeURIComponent(name);
  } catch (e) {
    return name;
  }
};

const ArtistModal = ({ isOpen, artistDetails, closeModal }) => {
  const navigate = useNavigate();
  if (!isOpen) return null;

  return (
    <Modal show={isOpen} onHide={closeModal} size="lg" centered className="artist-bio-modal">
      <Modal.Header closeButton>
        <Modal.Title>{decodeArtistName(artistDetails.artist_name)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="artist-bio-modal-card">
          {/* Artist Image */}
          <div className="artist-bio-modal-image-wrapper">
            <img
              src={artistImageUrl(artistDetails)}
              alt={decodeArtistName(artistDetails.artist_name)}
              className="artist-bio-modal-image"
              onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
              onLoad={(e) => { e.target.style.display = 'block'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'none'; }}
            />
            <div
              className="artist-bio-modal-image-placeholder"
              style={{ display: 'none' }}
            >
              <FaUser />
            </div>
          </div>

          {/* Artist Details */}
          <h2 className="artist-bio-modal-name">{decodeArtistName(artistDetails.artist_name)}</h2>

          <p className="artist-bio-modal-description-label">Description</p>
          <div className="artist-bio-modal-description">
            <span dangerouslySetInnerHTML={{ __html: artistDetails.artist_description }} />
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="artist-bio-modal-btn-close" onClick={closeModal}>
          Close
        </button>
        <button className="artist-bio-modal-btn-edit" onClick={() => navigate(`/edit-artist-bio?artist_id=${artistDetails.artist_id}`)}>
          <FaEdit /> Edit
        </button>
      </Modal.Footer>
    </Modal>
  );
};

//main component

const ArtistBio = () => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [ArtistBioData, setArtistBioData] = useState([]);
  const [page, setpage] = useState(1);
  const [limit, setlimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [debounceQuery, setDebounceQuery] = useState(searchQuery);
  const [totalCount, setTotalCount] = useState(0);

  const navigate = useNavigate();

  const fetchArtistBio = async (page, limit) => {
    setLoading(true);
    try {
      let response = await allAPiServicesCall.getAllPlaylistBio(
        { page, limit },
        getAuthConfig()
      );

      if (response.success && response.status === 200) {
        setArtistBioData(response.data.artistDetails);
        setTotalPages(response.data.totalPages);
        setTotalCount(response.data.totalCount || response.data.artistDetails.length);
      } else {
        console.warn("No data found or data is not an array");
      }
    } catch (error) {
      console.error("Error fetching artist bios:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (debounceQuery.trim()) {
      fetchSearchResults(debounceQuery);
    } else {
      fetchArtistBio(page, limit);
    }
  }, [page, limit, debounceQuery]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebounceQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleNextPage = () => {
    if (page < totalPages) {
      setpage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setpage((prevPage) => prevPage - 1);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  //fetch Artist Bio Details
  const fetchArtistBioDetails = async (artist_name) => {
    try {
      const response = await allAPiServicesCall.getArtistBioDetails(getAuthConfig(), artist_name)
      setSelectedArtist(response.data);
      setShowViewModal(true);
    } catch (error) {
      console.log("fetch artist error", error)
    }
  };

  //search artist api
  const fetchSearchResults = async (query) => {
    setLoading(true);
    try {
      let response = await allAPiServicesCall.getSearchArtist(
        { query },
        getAuthConfig()
      );
      const searchList = response.data || [];
      if (searchList.length > 0) {
        setSearchResults(searchList);
      } else {
        setSearchResults([]);
        toast.info("No results found");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to fetch search artists");
    }
  };

  const displayData = searchQuery.trim() ? searchResults : ArtistBioData;

  return (
    <CommonLayout>
      <div className="artist-bio-page">
        {/* Header Section */}
        <div className="artist-bio-header">
          <div className="artist-bio-header-content">
            <div className="artist-bio-header-icon">
              <FaMicrophone />
            </div>
            <div className="artist-bio-header-text">
              <h1 className="artist-bio-title">Artist Bio</h1>
              <p className="artist-bio-subtitle">
                Manage artist biographies and information
              </p>
            </div>
          </div>
          <div className="artist-bio-stats">
            <div className="artist-bio-stat-badge">
              <FaUsers />
              <span><strong>{totalCount}</strong> Artists</span>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <div className="artist-bio-search-section">
          <div className="artist-bio-search-wrapper">
            <FaSearch className="artist-bio-search-icon" />
            <input
              type="text"
              className="artist-bio-search-input"
              placeholder="Search for artists..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="artist-bio-loading">
            <div className="artist-bio-loading-spinner"></div>
            <p className="artist-bio-loading-text">Loading artists...</p>
          </div>
        ) : displayData.length === 0 ? (
          <div className="artist-bio-table-container">
            <div className="artist-bio-empty">
              <div className="artist-bio-empty-icon">
                <FaUser />
              </div>
              <p className="artist-bio-empty-text">
                {searchQuery ? "No artists match your search" : "No artists found"}
              </p>
            </div>
          </div>
        ) : (
          <div className="artist-bio-table-container">
            <table className="artist-bio-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Artist</th>
                  <th>Genre</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayData.map((artist) => (
                  <tr key={artist.artist_id || artist.id}>
                    <td className="artist-bio-image-cell">
                      <div className="artist-bio-image-wrapper">
                        <img
                          src={artistImageUrl(artist)}
                          alt={decodeArtistName(artist?.artist_name)}
                          className="artist-bio-image"
                          onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                          onLoad={(e) => { e.target.style.display = 'block'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'none'; }}
                        />
                        <div
                          className="artist-bio-image-placeholder"
                          style={{ display: 'none' }}
                        >
                          <FaUser />
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="artist-bio-name">{decodeArtistName(artist?.artist_name)}</span>
                    </td>
                    <td>
                      {artist?.genres ? (
                        <span className="artist-bio-genre-badge">{artist.genres}</span>
                      ) : (
                        <span className="artist-bio-genre-empty">-</span>
                      )}
                    </td>
                    <td>
                      <div className="artist-bio-actions">
                        <button
                          className="artist-bio-action-btn artist-bio-action-btn-view"
                          onClick={() => fetchArtistBioDetails(artist.artist_name)}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="artist-bio-action-btn artist-bio-action-btn-edit"
                          onClick={() => navigate(`/edit-artist-bio?artist_id=${artist.artist_id}`)}
                          title="Edit Artist"
                        >
                          <FaEdit />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {!searchQuery.trim() && ArtistBioData.length > 0 && totalPages > 1 && (
              <div className="artist-bio-pagination">
                <button
                  className="artist-bio-pagination-btn"
                  onClick={handlePreviousPage}
                  disabled={page === 1}
                >
                  <FaChevronLeft /> Previous
                </button>
                <div className="artist-bio-pagination-info">
                  <span className="artist-bio-pagination-current">{page}</span>
                  <span className="artist-bio-pagination-separator">/</span>
                  <span className="artist-bio-pagination-total">{totalPages}</span>
                </div>
                <button
                  className="artist-bio-pagination-btn"
                  onClick={handleNextPage}
                  disabled={page === totalPages}
                >
                  Next <FaChevronRight />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Component */}
      <ArtistModal
        isOpen={showViewModal}
        artistDetails={selectedArtist}
        closeModal={() => setShowViewModal(false)}
      />
    </CommonLayout>
  );
};

export default ArtistBio;
